import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Typography, Button, TextField } from '@mui/material';
import { Grid, IconButton } from "@material-ui/core";
import { alertActions, mapCenterAction, masterActions, userProfileAction } from '_store';
import { mapCenterAdminValidationSchema } from "_utils/validationSchema";
import { supportedFormat } from '_utils/constant';
import { base64ToFile } from '_utils';
import { AutocompleteInput, ModalPopup, UploadFiles } from '_components';
import { CompanyDetails, AdditionalDetails, CompanyPOC } from "container/user";
import { mapCenterRegistrationLabels } from '_utils/labels';
import { raphaelinfo, materialsymbolsdownload } from 'images';
import { green } from '@mui/material/colors';
import { CheckCircleRounded } from '@mui/icons-material';
import RejectReason from './RejectReason';
import RejectDetails from './RejectDetails';
import PartialApprovalModal from './PartialApprovalModal';
import dayjs from 'dayjs';
import { ERR_MSG } from 'constants/labels';

const UserProfileDetailsMCAdmin = ({ selectedUser, setSelectedUser, setshowDetailSection, handleReject, handleRefresh }) => {
    const dispatch = useDispatch();
    // const navigate = useNavigate();
    const header = 'Map Center';
    const portalkey = "mc";
    const id = selectedUser?.UserID;
    const [actionType, setActionType] = useState(null);
    const [open, setOpen] = useState(false);

    const authUser = useSelector(x => x.auth.value);
    const userAccess = authUser?.Data?.UserAccess;
    const userdetails = authUser?.Data?.UserDetails;
    const authUserName = `${userdetails.firstName} ${userdetails.lastName}`;
    const isReviewer = userAccess?.some(access => access.Role.toLowerCase().includes('reviewer'));
    const isAdmin = userAccess?.some(access => access.Role.toLowerCase().includes('admin'));
    const [initialData, setInitialData] = useState({});
    const [isModified, setIsModified] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [comment, setComment] = useState('');
    const [partialApprovalOpen, setPartialApprovalOpen] = useState(false);


    const user = useSelector(x => x.mapcenter?.userData);
    const statusID = user?.StatusID;   
    const [selectedDocumentType, setSelectedDocumentType] = useState(null);
    const [files, setFiles] = useState([]);

    const documentTypeData = user?.DocumentData || [];    
    const states = user?.State || [];
    const exsistingFiles = user?.FileData || [];
    const [isRejected, setIsRejected] = useState(false);
    const documentData = documentTypeData.map(x => ({
        label: x.DocumentDescription,
        value: x.DocumentTypeID
    }));

    const stateData = states.map(x => ({
        label: x.StateName,
        value: x.StateId
    }));

    const handlePartialApproveClick = () => {
        setPartialApprovalOpen(true);
    };

    const handlePartialApprovalConfirm = async (comments) => {
        dispatch(alertActions.clear());
        try {
            const transformedData = {                
                UserID: selectedUser?.UserID,
                Comment: comments,                                             
            };

            
            if(isReviewer) {
                const result = await dispatch(userProfileAction.partiallyVerifyMCUser(transformedData)).unwrap();            
                if (result?.Succeeded === false) {
                    dispatch(alertActions.error({ message: result?.Message, header: header }));
                    return;
                }                
                setPartialApprovalOpen(false);
                handleRefresh();
                dispatch(alertActions.success({ message: result?.Message, header: header, showAfterRedirect: true }));
            }     
            
            if(isAdmin) {
                const result = await dispatch(userProfileAction.partiallyApproveMCUser(transformedData)).unwrap();            
                if (result?.Succeeded === false) {
                    dispatch(alertActions.error({ message: result?.Message, header: header }));
                    return;
                }                
                setPartialApprovalOpen(false);
                handleRefresh();
                dispatch(alertActions.success({ message: result?.Message, header: header, showAfterRedirect: true }));
            } 
            
        } catch (error) {
            dispatch(alertActions.error({ message: error.message ? ERR_MSG["073"] : error, header }));
        }
    };

    const formatPhoneNumber = (number) => {
        const cleaned = ('' + number).replace(/\D/g, '');
        const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
        if (match) {
            return `${match[1]}-${match[2]}-${match[3]}`;
        }
        return number;
    };

    const { register, handleSubmit, control, reset, formState: { errors, isValid }, trigger, watch } = useForm({
        resolver: yupResolver(mapCenterAdminValidationSchema)
    });

    useEffect(() => {
        const fetchData = async () => {
            try {
                dispatch(alertActions.clear());
                dispatch(mapCenterAction.clear());
                const user = await dispatch(mapCenterAction.get({ id })).unwrap();
                const userData = user?.Data;
                const data = { ...userData, MobileNumber: formatPhoneNumber(userData.MobileNumber) };
                setInitialData(data);
                setIsLoading(false);
                reset(data);
                if (data?.FileData) {
                    setFiles(data?.FileData.map(file => ({
                        ID: file.ID,
                        AdditionalID: file.AdditionalID,
                        DocumentTypeID: file.DocumentTypeID,
                        FileName: file.FileName,
                        Format: file.Format,
                        Size: file.Size,
                        PortalKey: file.PortalKey,
                        File: file.File,
                        Url: file.Url
                    })));
                }
            } catch (error) {
                dispatch(alertActions.error({
                    message: error.message ? ERR_MSG["073"] : error,
                    header: header
                }));
                reset(user);
            }
        };
        fetchData();
    }, [id, dispatch, reset, portalkey]);


    useEffect(() => {
        const subscription = watch((currentData) => {
            if(!isLoading && Object.keys(initialData).length > 0) {
                const isChanged = !isDeepEqual(initialData, currentData);
                setIsModified(isChanged);
            }
        });
        return () => subscription.unsubscribe();
    }, [watch, isModified, isLoading]);

    const isDeepEqual = (obj1, obj2) => {
        if (obj1 === obj2) return true;

        if (typeof obj1 !== 'object' || typeof obj2 !== 'object' || obj1 === null || obj2 === null) {
            return false;
        }

        if (Array.isArray(obj1) && Array.isArray(obj2)) {
            if (obj1.length !== obj2.length) return false;
            for (let i = 0; i < obj1.length; i++) {
                
            if (!isDeepEqual(obj1[i], obj2[i])) return false;
            }
            return true;
        }

        if (Array.isArray(obj1) !== Array.isArray(obj2)) return false;

        const keys1 = Object.keys(obj1);
        const keys2 = Object.keys(obj2);

        if (keys1.length !== keys2.length) return false;

        for (let key of keys1) {
            if (!keys2.includes(key)) return false;
            if (!isDeepEqual(obj1[key], obj2[key])) {
                return false;
            }
        }
        return true;
    };

    const handleSaveClick = () => {
        setActionType('save');
    };

    // const handleApproveClick = () => {
    //     setActionType('approve', () => handleSubmit(onSubmit)());
    // };

    const handleVerifyClick = () => {
        setActionType('verify', () => handleSubmit(onSubmit)());
    };

    useEffect(() => {
        if (actionType) {
            handleSubmit(onSubmit)();
        }
    }, [actionType]);

    const onSubmit = async (data) => {
        dispatch(alertActions.clear());
        try {
            const isApprove = actionType === 'approve';

            if (isAdmin) {
                const missingDocumentTypes = documentTypeData.filter(docType =>
                    !files.some(file => file.DocumentTypeID === docType.DocumentTypeID)
                );

                if (!documentData || missingDocumentTypes.length > 0) {
                    const missingDescriptions = missingDocumentTypes.map(docType => docType.DocumentDescription).join(', ');
                    dispatch(alertActions.error({
                        message: `Missing files for document types: ${missingDescriptions}`,
                        header: header
                    }));
                    return;
                }
                const transformedData = saveData(data, isApprove);
                await dispatch(mapCenterAction.updateAdmin(transformedData)).unwrap();
            }
            else if (isReviewer) {
                const transformedData = {
                    UserID: id,
                    StatusID: 4,
                    ComapanyName: data.CompanyName,
                    CreatedBy: authUserName
                };
                await dispatch(userProfileAction.verifyUser(transformedData)).unwrap();
            }

            handleCancelClick();
            const message = isAdmin ?
                (isApprove ? mapCenterRegistrationLabels.message2 : mapCenterRegistrationLabels.message6)
                : mapCenterRegistrationLabels.message4;
            dispatch(alertActions.success({ message: message, header: header, showAfterRedirect: true }));

        } catch (error) {
            dispatch(alertActions.error({ message: error.message ? ERR_MSG["073"] : error, header }));
        }
        finally {
            setActionType(null); // Reset actionType after submission
        }
    };


    const saveData = (data, isApprove) => {
      // Validate that all required document types have files
      const formData = new FormData();

      formData.append("UserID", id);
      formData.append("FullName", data.FullName);
      formData.append("AlternateEmail", data.AlternateEmail);
      formData.append("DLState", data.DLState);
      formData.append("DLNumber", data.DLNumber);
      formData.append("HomeStreetAddress1", data.HomeStreetAddress1);
      formData.append("HomeStreetAddress2", data.HomeStreetAddress2 || "");
      formData.append("HomeCity", data.HomeCity);
      formData.append("HomeState", data.HomeState);
      formData.append("HomeZipCode", data.HomeZipCode);
      formData.append("CompanyName", data.CompanyName);
      formData.append("TaxIdentificationNumber", data.TaxIdentificationNumber);
      formData.append("CompanyStreetAddress1", data.CompanyStreetAddress1);
      formData.append("CompanyStreetAddress2", data.CompanyStreetAddress2 || "");
      formData.append("CompanyCity", data.CompanyCity);
      formData.append("CompanyState", data.CompanyState);
      formData.append("CompanyZipCode", data.CompanyZipCode);
      formData.append("CompanyContactName", data.CompanyContactName);
      formData.append("CompanyContactTelephone", data.CompanyContactTelephone);
      formData.append("CompanyContactEmailAddress", data.CompanyContactEmailAddress);
      formData.append("AuthorizedWGLContact", data.AuthorizedWGLContact);
      formData.append("AdditionalID", user?.AdditionalID || 0);
      formData.append("StatusID", user?.StatusID === 6 || isApprove ? 6 : 9);
      formData.append("MobileNumber", data.MobileNumber);
      formData.append("AlternatePhoneNumber", data.AlternatePhoneNumber);
      formData.append("EmailID", user.EmailID);
      formData.append("ModifiedBy", authUserName);    
      formData.append("ModifiedComment", comment);
        if (files && files.length > 0) {
            Array.from(files).forEach((file) => {
                if(file?.File) {
                    formData.append("FileData", file.File);
                    formData.append("DocumentTypeIDs", file.DocumentTypeID); 
                }                               
            });  
                      
        }
      return formData;
    };

    const handleBlur = async (e) => {
        const fieldName = e.target.name;
        await trigger(fieldName); // Trigger validation for the field
    };

    const handleOnChange = (event, newvalue) => {
        setSelectedDocumentType(newvalue);
    };

    const handleFileChange = (newFiles) => {
        setFiles(newFiles);
    };

    const handleDownload = async (fileName) => {
        try {
            if (!fileName) {
                throw new Error(ERR_MSG["073"]);
            }            
            await dispatch(mapCenterAction.downloadFile(fileName)).unwrap();
        } catch (error) {
            dispatch(
                alertActions.error({
                message: error.message ? ERR_MSG["073"] : error,
                header: "Map Center",
                })
            );
        }
    };

    const handleCancelClick = () => {
        setSelectedUser(null);
        setshowDetailSection(false);
        handleRefresh();
    }

    // const handleRejectClick = () => {
    //     setOpen(true);
    // };

    const handleClose = () => {
        setOpen(false);
    };

    const handleRejectConfirm = (reason, comments) => {
        handleReject(selectedUser, reason, comments);
        setOpen(false);
    };
    const handleRejected = (user) => {
        setIsRejected(true);
        setOpen(true);
    }
    return (
        <>
            {!(user?.loading) && (
                <Typography component="div" className="MapCenterAccecss">
                    <Typography component="div" className="MapCenterAccecssheading">
                        <Typography component="h1" variant="h5">Map Center Access</Typography>
                    </Typography>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <Typography className="Personal-Information-container" component="div">
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={12} md={12}>
                                    <Grid container spacing={3}>
                                        <Grid item xs={12} sm={12} md={8}>
                                            <Grid container spacing={3}>
                                                <Grid item xs={12} sm={12} md={12}>
                                                    <Typography component="div" className="mapcontainer">
                                                        <Typography component="div" className="Personal-Informationsheading">
                                                            <Typography component="h2" variant="h5">Registration Information</Typography>
                                                        </Typography>
                                                        <Typography component="div" className="passwordcheck">
                                                            <TextField
                                                                fullWidth
                                                                label="Date Submitted"
                                                                value={initialData?.CreatedOn ? dayjs(initialData.CreatedOn).format('MM/DD/YYYY') : ''}
                                                                disabled={true}
                                                                InputLabelProps={{
                                                                    shrink: true,
                                                                }}
                                                            />
                                                        </Typography>
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={6} md={6} className="Personal-Information">
                                                    <Typography component="div" className="mapcontainer">
                                                        <Typography component="div" className="Personal-Informationsheading">
                                                            <Typography component="h2" variant="h5">Personal Information</Typography>
                                                        </Typography>
                                                        <AdditionalDetails
                                                            handleBlur={handleBlur}
                                                            register={register}
                                                            control={control}
                                                            stateData={stateData}
                                                            errors={errors}
                                                            isAdmin={true}
                                                            isDisabled={isReviewer || isAdmin}
                                                        />
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={6} md={6} className="Personal-Information">
                                                    <Typography component="div" className="mapcontainer">
                                                        <Typography component="div" className="Personal-Informationsheading">
                                                            <Typography component="h2" variant="h5">Company Information</Typography>
                                                        </Typography>
                                                        <CompanyDetails
                                                            handleBlur={handleBlur}
                                                            register={register}
                                                            errors={errors}
                                                            control={control}
                                                            stateData={stateData}
                                                            isDisabled={isReviewer || isAdmin}
                                                        />
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={12} md={12}>
                                                    <CompanyPOC
                                                        register={register}
                                                        errors={errors}
                                                        control={control}
                                                        handleBlur={handleBlur}
                                                        isDisabled={isReviewer || isAdmin}
                                                        />
                                                </Grid>                                                
                                                {(statusID == 8) && <Grid item xs={12} sm={12} md={12}>
                                                    <RejectDetails
                                                        control={control}
                                                        register={register}
                                                        errors={errors}
                                                        initialData={initialData?.RejectionReason}
                                                    />
                                                </Grid>}
                                            </Grid>
                                        </Grid>
                                        {isAdmin && (<Grid item xs={12} sm={12} md={4}>
                                            <Typography component="div" className="UploadFiles-container mapcontainer  ">
                                                <Typography component="div" className="Personal-Informationsheading ">
                                                    <Typography component="h2" variant="h5">Document Upload  <img src={raphaelinfo} alt='raphaelinfo'></img></Typography>
                                                </Typography>
                                                <Typography component="div" className="passwordcheck marbottom0 selecticon">
                                                    <AutocompleteInput
                                                        control={control}
                                                        name="documentType"
                                                        label="Document Type"
                                                        options={documentData}
                                                        error={!!errors.documentType}
                                                        helperText={errors.documentType?.message}
                                                        handleBlur={handleBlur}
                                                        onChange={handleOnChange}
                                                    />
                                                </Typography>
                                                <UploadFiles
                                                    initialFiles={files}
                                                    portalKey={portalkey}
                                                    selectedDocumentType={selectedDocumentType}
                                                    supportedFormats={supportedFormat}
                                                    documentTypes={documentTypeData}
                                                    control={control}
                                                    errors={errors}
                                                    onFileChange={handleFileChange}
                                                    exsistingFiles={exsistingFiles}

                                                />
                                                <Typography component="div" className="SupportedFormats">
                                                    <Typography component="h3" >Download Template</Typography>
                                                    <div className="mar-top-16" >
                                                        <Typography component="div">Non-disclosure agreement
                                                            <IconButton onClick={handleDownload}>
                                                                <img src={materialsymbolsdownload} alt="material-symbols_download"></img>
                                                            </IconButton>
                                                        </Typography>
                                                    </div>
                                                </Typography>
                                           </Typography>
                                        </Grid>
                                        )}
                                        {isReviewer && (
                                        <Grid item xs={12} sm={12} md={4}>

                                            <Typography component="div" className="SupportedFormats Personal-Informationsheading">
                                                <Typography component="h2">Uploaded Documents</Typography>
                                                <Typography component="div" className="fileformat">
                                                    {documentTypeData && documentTypeData.map(type => {
                                                        const uploadedDocument = files.filter(x => x.DocumentTypeID === type.DocumentTypeID);
                                                        if (uploadedDocument && uploadedDocument.length > 0) {
                                                            return (
                                                                <div className="mar-top-16" key={type.DocumentTypeID}>
                                                                    <Typography component="div" >
                                                                        <CheckCircleRounded fontSize="small" style={{ color: green[500] }} />
                                                                        <Typography component="span" className="DocumentDescription">{type.DocumentDescription}</Typography>
                                                                        <Typography component="div" className="DocumentTypeID">
                                                                            <Grid container>
                                                                                <Typography component="div" className="textwordwrap">{uploadedDocument[0].FileName}</Typography>

                                                                                <IconButton onClick={() => handleDownload(uploadedDocument[0].Url)}>

                                                                                    <img src={materialsymbolsdownload} alt='download'></img>
                                                                                </IconButton>
                                                                            </Grid>
                                                                        </Typography>
                                                                    </Typography>
                                                                </div>
                                                            );
                                                        } else {
                                                            return (
                                                                <div className="mar-top-16" key={type.DocumentTypeID}>
                                                                    <Typography component="div"><CheckCircleRounded fontSize="small" color="disabled" /><Typography component="span" className="DocumentDescription"> {type.DocumentDescription}</Typography></Typography>
                                                                </div>
                                                            );
                                                        }
                                                    })}
                                                </Typography>
                                            </Typography>
                                        </Grid>
                                    )}
                                    </Grid>
                                </Grid>

                            </Grid>
                        </Typography>

                        <Grid item xs={12} sm={12} md={12} className="userprofilebutton">
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={12} md={6} >
                                    <Button variant="contained" color="red" className="cancelbutton" onClick={handleCancelClick}>
                                        Back
                                    </Button>
                                </Grid>
                                <Grid item xs={12} sm={12} md={6} >
                                    {isAdmin &&
                                        (<>
                                            <Button
                                                type="button"
                                                variant="contained" className='submitbutton save' color="primary"
                                                disabled={!isValid && ![4, 9, 6].includes(statusID) && !isModified}
                                                onClick={handleSaveClick}>
                                                Save
                                            </Button>
                                            <Button
                                                type="button"
                                                sx={{marginRight: "20px !important", width: "250px !important"}}
                                                variant="contained" 
                                                className='submitbutton save' 
                                                color="primary"
                                                onClick={handlePartialApproveClick} 
                                                disabled={statusID !== 4}>
                                                Partially Approve
                                            </Button>
                                        </>)}
                                    {(isReviewer) && (<>
                                        <Button
                                            type="button"
                                            variant="contained" className='submitbutton save' color="primary"
                                            onClick={handleVerifyClick} disabled={statusID !== 1}>
                                            Verify
                                        </Button>
                                        <Button
                                            type="button"
                                            sx={{marginRight: "20px !important"}}
                                            variant="contained" 
                                            className='submitbutton save' 
                                            color="primary"
                                            onClick={handlePartialApproveClick} 
                                            disabled={statusID !== 1}>
                                            Partially Verify
                                        </Button>
                                        <Button type="button" variant="contained" className="delete" //onClick={() => handleRejectClick()}
                                            onClick={() => handleRejected(selectedUser)} disabled={statusID !== 1}>
                                            Reject
                                        </Button>
                                    </>
                                    )}
                                </Grid>
                            </Grid>
                        </Grid>
                        {partialApprovalOpen && (
                            <PartialApprovalModal 
                                action={isReviewer ? "PARTIALVERIFY" : "PARTIALAPPROVE"}                                
                                open={partialApprovalOpen} 
                                onConfirm={handlePartialApprovalConfirm} 
                                handleClose={() => setPartialApprovalOpen(false)}
                            />
                        )}
                        {isRejected && <RejectReason open={open} id={id} initialData={initialData?.RejectionReason} onConfirm={handleRejectConfirm} handleClose={handleClose}></RejectReason>}

                    </form>
                </Typography>
            )}
        </>
    );
};

export default UserProfileDetailsMCAdmin;